import React, { useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'
import queryString from 'query-string'

import { DeckSearchTypeV3, DeckSearchV3HumanName, generateDefaultDeckSearchV3 } from 'types/search'
import { HUMAN_READABLE_FORMAT } from 'types/deck'

import { ORDER_BY_COOKIE_KEY } from 'pages/search/decks'

import PhatInput from 'components/formElements/PhatInput'
import PhatDropdown from 'components/formElements/PhatDropdown'
import PhatButton from 'components/formElements/PhatButton'
import Accordion from 'components/elements/Accordion'
import CardAutocomplete from 'components/formElements/CardAutocomplete'
import FormatDropdown from 'components/cardSearchPanel/searchFormElements/formatDropdown'
import DeckTagSelector from 'components/formElements/DeckTagSelector'
import UserAutocomplete from 'components/formElements/UserAutocomplete'
import Icon from 'components/elements/Icon'
import ColorToggles from 'components/cardSearchPanel/searchFormElements/colorToggles'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'
import Link from 'components/elements/Link'
import BracketDropdown from 'components/misc/BracketDropdown'

import styles from './deckSearchFormV3.module.scss'

type Props = {
  totalResults?: number
  className?: string
  hideTotal?: boolean
  accordionOpenByDefault?: boolean
}

const DeckSearchFromV3 = ({ totalResults, className = '', hideTotal = false, accordionOpenByDefault }: Props) => {
  const router = useRouter()

  const commanderRef = useRef<any>(null)
  const cardRef = useRef<any>(null)

  const [{ tbId: userId, tbUser: username, [ORDER_BY_COOKIE_KEY]: cachedOrderBy }, setCookie] = useCookies([
    'tbId',
    'tbUser',
    ORDER_BY_COOKIE_KEY,
  ])

  const [searchedDeckState] = useState<DeckSearchTypeV3>(
    generateDefaultDeckSearchV3(router.query, router.pathname, cachedOrderBy)
  )
  const [deckSearchState, setDeckSearchState] = useState<DeckSearchTypeV3>(
    generateDefaultDeckSearchV3(router.query, router.pathname, cachedOrderBy)
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const tiddyParams: Record<string, any> = { ...deckSearchState }

    // Clean up params so they don't make it into the URL
    for (const key of Object.keys(tiddyParams)) {
      const currentValue = tiddyParams[key]

      if (currentValue == null || currentValue == '' || currentValue == undefined) {
        delete tiddyParams[key]
      }
    }

    // Note that if they're searching, we always want the page to be the first for the "new" results
    router.push(`/search/decks/?${queryString.stringify({ ...tiddyParams, page: 1 })}`)
  }

  const handleUpdateSearch = (updatedParts: Partial<DeckSearchTypeV3>) =>
    setDeckSearchState({ ...deckSearchState, ...updatedParts })

  const handleDismissFilter = (paramToRemove: string) => {
    if (paramToRemove === 'orderBy') setCookie(ORDER_BY_COOKIE_KEY, '', { path: '/' })

    router.push(`/search/decks/?${queryString.stringify({ ...searchedDeckState, [paramToRemove]: undefined })}`)
  }

  const isDescending = (deckSearchState.orderBy || '-')[0] === '-'
  const handleFlipAscDesc = (current: string = '') => (current[0] === '-' ? current.replace('-', '') : `-${current}`)
  const edited = JSON.stringify(searchedDeckState) !== JSON.stringify(deckSearchState)
  const searchType = deckSearchState.packages ? 'packages' : 'decks'

  const activeFilters = Object.keys(searchedDeckState)
    // @ts-ignore - Seriously TS. The type matches keys from the object
    .map((searchParam: keyof DeckSearchTypeV3) => {
      let searchValue = searchedDeckState[searchParam]
      const searchName = DeckSearchV3HumanName[searchParam]

      if (!searchName) return null
      if (typeof searchParam === 'string' && !searchValue) return null

      // Overrides to more sane values where it makes sense to do so
      // @ts-ignore - searchValue comes in as a string, not a FormatValue
      if (searchParam === 'deckFormat') searchValue = HUMAN_READABLE_FORMAT[Number(searchValue)] || searchValue

      return (
        <div key={searchParam} className={styles.filter}>
          <button onClick={() => handleDismissFilter(searchParam)} className={styles.filterClose}>
            <Icon name="close" />
          </button>{' '}
          <span>
            {searchName}: {`${searchValue}`}
          </span>
        </div>
      )
    })
    .filter(element => element != null)

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.primaryControls}>
        <form className={styles.right} onSubmit={handleSubmit}>
          <PhatInput
            header="Deck name"
            placeholder="Super awesome deck name!"
            containerClassName={styles.searchBar}
            value={deckSearchState.name}
            onChange={name => handleUpdateSearch({ name })}
          />
          <PhatButton icon="search" type="submit" color={edited ? 'green' : undefined}>
            <span className={styles.searchButtonText}>Search decks</span>
          </PhatButton>
        </form>

        <div className={styles.left}>
          <PhatDropdown
            header="Order by"
            value={deckSearchState.orderBy?.replace('-', '') || 'updatedAt'}
            options={[
              { label: 'Alphabetical', id: 'name', onClick: () => handleUpdateSearch({ orderBy: 'name' }) },
              { label: 'Updated At', id: 'updatedAt', onClick: () => handleUpdateSearch({ orderBy: '-updatedAt' }) },
              { label: 'Created At', id: 'createdAt', onClick: () => handleUpdateSearch({ orderBy: '-createdAt' }) },
              { label: 'Views', id: 'viewCount', onClick: () => handleUpdateSearch({ orderBy: '-viewCount' }) },
              { label: 'Size', id: 'size', onClick: () => handleUpdateSearch({ orderBy: '-size' }) },
              { label: 'EDH Bracket', id: 'edhBracket', onClick: () => handleUpdateSearch({ orderBy: 'edhBracket' }) },
            ]}
          />
          <PhatDropdown
            icon={isDescending ? 'sort content descending' : 'sort content ascending'}
            value={isDescending ? 'Descending' : 'Ascending'}
            options={[
              {
                label: 'Descending',
                id: 'Descending',
                onClick: () =>
                  handleUpdateSearch({
                    orderBy: isDescending ? deckSearchState.orderBy : handleFlipAscDesc(deckSearchState.orderBy),
                  }),
              },
              {
                label: 'Ascending',
                id: 'Ascending',
                onClick: () =>
                  handleUpdateSearch({
                    orderBy: !isDescending ? deckSearchState.orderBy : handleFlipAscDesc(deckSearchState.orderBy),
                  }),
              },
            ]}
            menuClassName={styles.orderMenu}
          />
        </div>
      </div>

      {!hideTotal && <div className={styles.results}>Results: {totalResults === 1000 ? '1000+' : totalResults}</div>}
      <div className={styles.filters}>
        {activeFilters}
        {activeFilters.length > 1 && (
          <Link href={'/search/decks'} className={styles.clearAllFiltersButton}>
            Clear all filters
          </Link>
        )}
      </div>

      <Accordion label="Advanced options" containerClass={styles.advancedOptions} defaultOpen={accordionOpenByDefault}>
        <div className={styles.colorOptions}>
          <PhatDropdown
            header="Color options"
            value={deckSearchState.colorsInclude ? 'includes' : 'exact'}
            options={[
              {
                id: 'exact',
                label: 'Exactly match colors',
                onClick: () => handleUpdateSearch({ colorsInclude: false }),
              },
              {
                id: 'includes',
                label: 'Include selected colors',
                onClick: () => handleUpdateSearch({ colorsInclude: true }),
              },
            ]}
          />

          <ColorToggles
            stringValue={deckSearchState.colors}
            onStringValueChange={colors => handleUpdateSearch({ colors })}
            className={styles.colorToggles}
          />
        </div>

        <FormatDropdown
          header="Deck format"
          disabled={deckSearchState.packages}
          value={deckSearchState.deckFormat}
          onChange={deckFormat => handleUpdateSearch({ deckFormat })}
          className={styles.formatPicker}
        />

        <BracketDropdown
          header="Commander Bracket"
          value={deckSearchState.edhBracket}
          onChange={edhBracket => handleUpdateSearch({ edhBracket })}
          className={styles.formatPicker}
        />

        <CardAutocomplete
          fetchCard
          ref={commanderRef}
          disabled={deckSearchState.packages}
          onFocus={() => commanderRef.current.select()}
          header="Commander"
          placeholder="Ezuri, Claw of Progress"
          value={deckSearchState.commanderName}
          onChange={(_, { value: commanderName }) => handleUpdateSearch({ commanderName })}
          onSelect={card => handleUpdateSearch({ commanderName: card.name })}
        />

        <CardAutocomplete
          fetchCard
          ref={cardRef}
          onFocus={() => cardRef.current.select()}
          header="Card"
          placeholder="Sol Ring"
          value={deckSearchState.cardName}
          onChange={(_, { value: cardName }) => handleUpdateSearch({ cardName })}
          onSelect={card => handleUpdateSearch({ cardName: card.name })}
        />

        <DeckTagSelector
          label="Deck tag"
          onChange={deckTagName => handleUpdateSearch({ deckTagName })}
          onSelect={(_, deckTagName) => handleUpdateSearch({ deckTagName })}
        />

        <UserAutocomplete
          label="Username"
          placeholder="UsernameGoBurrr"
          value={deckSearchState.ownerUsername}
          onChange={ownerUsername => handleUpdateSearch({ ownerUsername })}
          onSelected={owner => handleUpdateSearch({ ownerUsername: owner.username })}
        />

        <div className={styles.deckSize}>
          <PhatDropdown
            value={deckSearchState.sizeLogic || 'EQ'}
            header="Deck size"
            options={[
              { id: 'EQ', label: 'Equals', onClick: () => handleUpdateSearch({ sizeLogic: 'EQ' }) },
              { id: 'GTE', label: 'Greater-than equals', onClick: () => handleUpdateSearch({ sizeLogic: 'GTE' }) },
              { id: 'LTE', label: 'Less-than equals', onClick: () => handleUpdateSearch({ sizeLogic: 'LTE' }) },
            ]}
          />

          <PhatInput
            type="number"
            value={deckSearchState.size}
            placeholder="100"
            onChange={size => handleUpdateSearch({ size })}
          />
        </div>

        {!!userId && (
          <div className={styles.userFlags}>
            <CheckboxFancybox
              header={`My ${searchType} only`}
              description="Search decks you own only"
              checked={deckSearchState.ownerUsername === username}
              onClick={() =>
                handleUpdateSearch({ ownerUsername: deckSearchState.ownerUsername === username ? undefined : username })
              }
            />

            <CheckboxFancybox
              header={`Bookmarked ${searchType}`}
              description={`Search for ${searchType} that ${username} has bookmarked`}
              checked={deckSearchState.bookmarks || false}
              onClick={() => handleUpdateSearch({ bookmarks: !deckSearchState.bookmarks })}
            />

            <CheckboxFancybox
              header="Shared with me"
              description={`Search ${searchType} that have been shared with ${username} via the collaborators feature`}
              checked={deckSearchState.shared || false}
              onClick={() => handleUpdateSearch({ shared: !deckSearchState.shared })}
            />

            <CheckboxFancybox
              header="Users I follow"
              description={`Search ${searchType} that are owned by who ${username} follows`}
              checked={deckSearchState.followed || false}
              onClick={() => handleUpdateSearch({ followed: !deckSearchState.followed })}
            />

            <CheckboxFancybox
              header={`Recent ${searchType}`}
              description={`Search ${searchType} that ${username} has recently viewed`}
              checked={deckSearchState.recentId || false}
              onClick={() => handleUpdateSearch({ recentId: !deckSearchState.recentId })}
            />

            <CheckboxFancybox
              header="Card packages"
              description={`Search card packages rather than decks`}
              checked={deckSearchState.packages || false}
              onClick={() => handleUpdateSearch({ packages: !deckSearchState.packages })}
            />
          </div>
        )}
      </Accordion>
    </div>
  )
}

export default DeckSearchFromV3
